import React from 'react';
import PropTypes from 'prop-types';
import {Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';

// Maria, 19 October 2026: the staff Next of Kin and Other Contacts tables fill the width of the page and line up with each
// other. The room is shared out in the same proportions in both (name, phone number, then address or email), the buttons
// are only as wide as they need, and a column is never narrower than its heading or a phone number or email address on
// one line; only the name wraps when the page is too narrow. The Add buttons go under the table, not in it.
const HEAD_SX = {backgroundColor: '#e3f5f1', fontWeight: 600, py: 1, lineHeight: 1.25, whiteSpace: 'nowrap'};
const CELL_SX = {py: 0.5, lineHeight: 1.25};
// the buttons column is the same width in both tables, wide enough for Edit and Delete, so the tables line up
const ACTIONS_SX = {width: 160, minWidth: 160, whiteSpace: 'nowrap'};
// Maria, 19 October 2026: the Email and Address columns are the same fixed width, wide enough for an email address
const COLUMN_SHARES = ['55%', '45%', 300];
const shareOf = index => ({
    width: COLUMN_SHARES[index] ?? 'auto',
    ...(typeof COLUMN_SHARES[index] === 'number' ? {minWidth: COLUMN_SHARES[index], maxWidth: COLUMN_SHARES[index]} : {}),
    ...(index > 0 ? {whiteSpace: 'nowrap'} : {})
});

// 'dense' (Next of Kin) starts each row's text at the top, so the name and phone number are level with the first line
// of the address
const StaffContactTable = ({columns, rows, rowKey, actions, emptyText, dense = false}) => (
    // on a phone or small tablet the table scrolls sideways inside its box rather than squeezing its columns
    <Paper variant="outlined" sx={{overflowX: {xs: 'auto', md: 'hidden'}, overflowY: 'hidden'}}>
        <Table size="small" sx={{tableLayout: 'auto', width: '100%', minWidth: {xs: 600, md: 0}}}>
            <TableHead>
                <TableRow>
                    {columns.map((column, index) => (
                        <TableCell key={column.id} sx={{...HEAD_SX, ...shareOf(index)}}>{column.header}</TableCell>
                    ))}
                    {actions && <TableCell sx={{...HEAD_SX, ...ACTIONS_SX}}>Actions</TableCell>}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={columns.length + (actions ? 1 : 0)} sx={{...CELL_SX, borderBottom: 0}}>
                            <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
                        </TableCell>
                    </TableRow>
                )}
                {rows.map((row, index) => (
                    <TableRow key={rowKey(row)} sx={index === rows.length - 1 ? {'& td': {borderBottom: 0}} : undefined}>
                        {columns.map((column, columnIndex) => (
                            <TableCell
                                key={column.id}
                                sx={{
                                    ...CELL_SX,
                                    verticalAlign: dense ? 'top' : 'middle',
                                    // a very long email address wraps inside its fixed column rather than pushing the table wider
                                    ...(columnIndex === 0 ? {overflowWrap: 'break-word'} : typeof COLUMN_SHARES[columnIndex] === 'number' ? {overflowWrap: 'anywhere'} : {whiteSpace: 'nowrap'})
                                }}
                            >
                                {column.cell(row)}
                            </TableCell>
                        ))}
                        {actions && (
                            <TableCell sx={{...CELL_SX, ...ACTIONS_SX, verticalAlign: dense ? 'top' : 'middle'}}>
                                {actions(row)}
                            </TableCell>
                        )}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </Paper>
);

StaffContactTable.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        header: PropTypes.string.isRequired,
        cell: PropTypes.func.isRequired
    })).isRequired,
    rows: PropTypes.array.isRequired,
    rowKey: PropTypes.func.isRequired,
    actions: PropTypes.func,
    emptyText: PropTypes.string,
    dense: PropTypes.bool
};

export default StaffContactTable;
